// Increment 45a: platform detection + native-aware secure token storage.
// On native (Capacitor) we persist the auth token in @capacitor/preferences
// (backed by the OS keystore/UserDefaults) and mirror it into an in-memory cache
// so the synchronous getToken() path used across the API client keeps working.
// On web we fall back to localStorage exactly as before.
import { Capacitor } from "@capacitor/core";

export const isNativeApp = (): boolean => {
  try { return Capacitor.isNativePlatform(); } catch { return false; }
};

export const platformName = (): "ios" | "android" | "web" => {
  try { return Capacitor.getPlatform() as any; } catch { return "web"; }
};

const TOKEN_KEY = "kpv3_token";
let memToken: string | null = null;

// Prime the in-memory token from native secure storage at startup (async).
export async function primeNativeToken(): Promise<void> {
  if (!isNativeApp()) return;
  try {
    const { Preferences } = await import("@capacitor/preferences");
    const { value } = await Preferences.get({ key: TOKEN_KEY });
    memToken = value ?? null;
  } catch { /* keep memToken as-is */ }
}

/** Synchronous token read used by the API client. */
export function readToken(): string | null {
  if (isNativeApp()) return memToken;
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
}

/** Persist (or clear) the token to the platform's secure store. */
export function writeToken(token: string | null): void {
  if (isNativeApp()) {
    memToken = token;
    // fire-and-forget secure persistence
    import("@capacitor/preferences").then(({ Preferences }) => {
      if (token) void Preferences.set({ key: TOKEN_KEY, value: token });
      else void Preferences.remove({ key: TOKEN_KEY });
    }).catch(() => {});
    return;
  }
  try { token ? localStorage.setItem(TOKEN_KEY, token) : localStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ }
}
